import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Drawer } from "../Drawer";
import { trpc } from "../../lib/trpc";

interface CertCreateProps {
  open: boolean;
  onClose: () => void;
}

const CERT_TYPES = ["HACCP", "ISO22000", "Helal", "Organik", "CoA", "Diğer"];

export function CertificateCreateDrawer({ open, onClose }: CertCreateProps) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [certType, setCertType] = useState("HACCP");
  const [scope, setScope] = useState<"company" | "item" | "supplier" | "lot">("company");
  const [issuer, setIssuer] = useState("");
  const [issuedAt, setIssuedAt] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [notes, setNotes] = useState("");
  const [submitErr, setSubmitErr] = useState<string | null>(null);

  const createMut = trpc.certificate.create.useMutation();

  useEffect(() => {
    if (open) {
      setName(""); setCertType("HACCP"); setScope("company"); setIssuer("");
      setIssuedAt(""); setValidUntil(""); setNotes(""); setSubmitErr(null);
      createMut.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const errorMessage = useMemo(() => submitErr ?? createMut.error?.message ?? null, [submitErr, createMut.error]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitErr(null);
    if (!name.trim()) return setSubmitErr("Sertifika adı zorunlu.");
    try {
      await createMut.mutateAsync({
        name: name.trim(), certType, scope,
        issuer: issuer || undefined,
        issuedAt: issuedAt ? new Date(issuedAt).toISOString() : undefined,
        validUntil: validUntil ? new Date(validUntil).toISOString() : undefined,
        notes: notes || undefined,
      });
      await qc.invalidateQueries();
      onClose();
    } catch { /* err banner */ }
  }

  return (
    <Drawer
      open={open} onClose={onClose} title="Yeni Sertifika" subtitle="HACCP / ISO 22000 / Helal / CoA kaydı"
      footer={
        <>
          <button type="button" className="btn-md ghost" onClick={onClose} disabled={createMut.isPending}>İptal</button>
          <button type="submit" form="cert-form" className="btn-md primary" disabled={createMut.isPending}>
            {createMut.isPending ? "Kaydediliyor…" : "Kaydet"}
          </button>
        </>
      }
    >
      <form id="cert-form" onSubmit={handleSubmit} noValidate>
        {errorMessage ? <div className="form-banner-err">{errorMessage}</div> : null}
        <div className="form-field">
          <label className="lbl" htmlFor="c-name">Sertifika Adı <span className="req">*</span></label>
          <input id="c-name" className="form-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={200} required placeholder="Örn. HACCP Belgesi 2026" />
        </div>
        <div className="form-row">
          <div className="form-field">
            <label className="lbl" htmlFor="c-type">Tür <span className="req">*</span></label>
            <select id="c-type" className="form-select" value={certType} onChange={(e) => setCertType(e.target.value)}>
              {CERT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="form-field">
            <label className="lbl" htmlFor="c-scope">Kapsam</label>
            <select id="c-scope" className="form-select" value={scope} onChange={(e) => setScope(e.target.value as typeof scope)}>
              <option value="company">Firma Geneli</option>
              <option value="item">Ürün</option>
              <option value="supplier">Tedarikçi</option>
              <option value="lot">Lot (CoA)</option>
            </select>
          </div>
        </div>
        <div className="form-field">
          <label className="lbl" htmlFor="c-issuer">Veren Kurum</label>
          <input id="c-issuer" className="form-input" value={issuer} onChange={(e) => setIssuer(e.target.value)} maxLength={200} placeholder="Örn. TSE, Helal Akreditasyon" />
        </div>
        <div className="form-row">
          <div className="form-field">
            <label className="lbl" htmlFor="c-issued">Veriliş Tarihi</label>
            <input id="c-issued" className="form-input" type="date" value={issuedAt} onChange={(e) => setIssuedAt(e.target.value)} />
          </div>
          <div className="form-field">
            <label className="lbl" htmlFor="c-valid">Geçerlilik Sonu</label>
            <input id="c-valid" className="form-input" type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
            <span className="help">Süresi yaklaşan sertifikalar uyarı verir.</span>
          </div>
        </div>
        <div className="form-field">
          <label className="lbl" htmlFor="c-notes">Notlar</label>
          <textarea id="c-notes" className="form-textarea" value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={1000} rows={2} />
        </div>
        <div style={{ fontSize: 11.5, color: "var(--neutral-500)" }}>
          Belge dosyası (PDF) yükleme Faz 10'da gelecek — şimdilik metadata kaydı.
        </div>
      </form>
    </Drawer>
  );
}
